import { useEffect, useState, useRef, useCallback } from 'react';
import { getAdminPolicies, savePolicy, deletePolicy, importPolicies, GetAdminPoliciesOutputType } from 'zite-endpoints-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, FileText, Users, Upload, X, FileUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownEditor } from '@/components/markdown-editor';

type Policy = GetAdminPoliciesOutputType['policies'][0];

export default function PoliciesAdminTab() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editPolicy, setEditPolicy] = useState<Policy | null>(null);
  const [form, setForm] = useState({ policyName: '', body: '', status: 'Draft' as 'Draft' | 'Published', attachmentUrl: '' });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    getAdminPolicies({}).then(d => setPolicies(d.policies)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditPolicy(null);
    setForm({ policyName: '', body: '', status: 'Draft', attachmentUrl: '' });
    setSheetOpen(true);
  };

  const openEdit = (p: Policy) => {
    setEditPolicy(p);
    setForm({ policyName: p.policyName, body: p.body, status: p.status as any, attachmentUrl: p.attachmentUrl || '' });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.policyName.trim()) { toast.error('Policy name is required'); return; }
    setSaving(true);
    try {
      await savePolicy({ id: editPolicy?.id, policyName: form.policyName, body: form.body, status: form.status, attachmentUrl: form.attachmentUrl || undefined });
      toast.success(editPolicy ? 'Policy updated' : 'Policy created');
      setSheetOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Select a PDF or Word file first'); return; }
    setImporting(true);
    const tid = 'import-policies';
    toast.loading('Uploading and extracting policies…', { id: tid });
    try {
      const { fileUrl } = await uploadFile({ data: importFile, filename: importFile.name });
      const result = await importPolicies({ pdfUrl: fileUrl, pdfFilename: importFile.name });
      toast.success(`✓ Imported ${result.count} ${result.count === 1 ? 'policy' : 'policies'}: ${result.policyNames.join(', ')}`, { id: tid });
      setImportOpen(false);
      setImportFile(null);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Import failed', { id: tid });
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.pdf') || file.name.endsWith('.docx'))) setImportFile(file);
    else toast.error('Please drop a PDF or Word (.docx) file');
  }, []);

  const handleDelete = async (id: string) => {
    try { await deletePolicy({ id }); toast.success('Policy deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" /> Company Policies
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">{policies.filter(p => p.status === 'Published').length} published · {policies.filter(p => p.status === 'Draft').length} drafts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Import PDF
          </Button>
          <Button size="sm" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Add Policy</Button>
        </div>
      </div>

      {policies.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-16 text-center">
          <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">No policies yet</p>
          <Button size="sm" className="mt-2" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Add First Policy</Button>
        </div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {policies.map(p => (
            <div key={p.id} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{p.policyName}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className={`text-xs ${p.status === 'Published' ? 'text-primary' : 'text-muted-foreground'}`}>{p.status}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="w-3 h-3" /> {p.acknowledgedCount} / {p.totalStaff} acknowledged
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-primary h-full rounded-full"
                    style={{ width: p.totalStaff > 0 ? `${(p.acknowledgedCount / p.totalStaff) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)}><Pencil className="w-3 h-3" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete "{p.policyName}"?</AlertDialogTitle></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(p.id)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}

      {/* AI Import Sheet */}
      <Sheet open={importOpen} onOpenChange={v => { if (!importing) { setImportOpen(v); if (!v) setImportFile(null); } }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI Import Policies</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="border border-primary/20 bg-primary/5 rounded-lg px-3 py-2 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Auto-normalization applied:</p>
              <p>• Company name → <strong>Rattlebone Sherston Ltd</strong></p>
              <p>• "the manager" / "your manager" → <strong>your line manager</strong></p>
            </div>
            {importFile ? (
              <div className="flex items-center gap-3 border border-primary/30 bg-primary/5 rounded-lg px-3 py-2.5">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{importFile.name}</p>
                  <p className="text-xs text-muted-foreground">{(importFile.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
                <button onClick={() => setImportFile(null)} className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div onDragOver={e => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}>
                <Upload className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Drop policy PDF or Word document</p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse · PDF or .docx</p>
                <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setImportFile(f); }} />
              </div>
            )}
            <p className="text-xs text-muted-foreground">AI will read the document, extract individual policies, and normalize company name and manager references automatically. Imported policies are saved as Drafts for your review.</p>
            <Button onClick={handleImport} disabled={importing || !importFile} className="w-full">
              {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting policies…</> : <><Sparkles className="w-4 h-4 mr-2" /> Extract & Import Policies</>}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader><SheetTitle>{editPolicy ? 'Edit Policy' : 'New Policy'}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Policy Name *</label>
              <Input value={form.policyName} onChange={e => setForm(f => ({ ...f, policyName: e.target.value }))} placeholder="e.g. Anti-Harassment Policy" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Body</label>
              <MarkdownEditor content={form.body} onChange={v => setForm(f => ({ ...f, body: v }))} height="dynamic-lg" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Attachment URL (optional)</label>
              <Input value={form.attachmentUrl} onChange={e => setForm(f => ({ ...f, attachmentUrl: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v as any }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editPolicy ? 'Update Policy' : 'Create Policy'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
